import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  valueForOnlyNumericInputTextFields,
  valueForOnlyStringInputTextFields,
} from "utils/helpers";

import {
  selectIsAddModalOpen,
  selectRoofTypeForm,
  selectRoofTypeSaving,
} from "../../redux/selector";
import { roofTypeActions } from "../../redux/slice";
import { FIELD_KIND, FieldKind, RoofTypeForm } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof RoofTypeForm;
  label: string;
};

const isNumberField = (name: keyof RoofTypeForm) =>
  FIELD_KIND[name] === FieldKind.Number;

const normalizeRoofTypeFieldValue = (
  name: keyof RoofTypeForm,
  value: string,
) => {
  if (isNumberField(name)) {
    return valueForOnlyNumericInputTextFields(value);
  }
  return valueForOnlyStringInputTextFields(value);
};

const FIELDS: FieldConfig[] = [
  { key: "number", label: "NUMBER" },
  { key: "description", label: "DESCRIPTION" },
  { key: "maxHeight", label: "MAX_HEIGHT" },
];

const REQUIRED_FIELDS: Array<keyof RoofTypeForm> = [
  "number",
  "description",
  "maxHeight",
];

const ROOF_TYPE_MODAL = "ROOF_TYPE.MODAL";

const AddRoofTypeModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectRoofTypeForm);
  const saving = useSelector(selectRoofTypeSaving);

  const onClose = () => {
    if (saving) return;
    dispatch(roofTypeActions.closeAddModal());
  };

  const setField = (name: keyof RoofTypeForm, value: string) =>
    dispatch(
      roofTypeActions.setFormField({
        name,
        value: normalizeRoofTypeFieldValue(name, value),
      }),
    );

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key].trim()));

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>
            {t(`${ROOF_TYPE_MODAL}.ADD_TITLE`)}
          </Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {FIELDS.map(({ key, label }) => (
            <Box key={key} className={styles.field}>
              <Typography className={styles.label}>
                {t(`${ROOF_TYPE_MODAL}.FIELDS.${label}`)}
              </Typography>
              <TextField
                className={styles.input}
                fullWidth
                variant="outlined"
                placeholder={t(`${ROOF_TYPE_MODAL}.PLACEHOLDERS.ENTER_${label}`)}
                value={form[key]}
                disabled={saving}
                InputProps={{ notched: false }}
                {...(isNumberField(key) ? { inputMode: "numeric" as const } : {})}
                onChange={(event) => setField(key, event.target.value)}
              />
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={() => dispatch(roofTypeActions.addRoofType())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddRoofTypeModal;
